import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  FC,
  ReactNode,
} from 'react';
import { useDispatch } from 'react-redux';

import { open, close } from 'src/common/ducks/zippy';

type OffSeasonSummaryContextValue = {
  selectedBoatHash: string | null;
  openSummary: (boatHash: string) => void;
  closeSummary: () => void;
};

const OffSeasonSummaryContext = createContext<OffSeasonSummaryContextValue>({
  selectedBoatHash: null,
  openSummary: () => {},
  closeSummary: () => {},
});

export const useOffSeasonSummaryContext = () => useContext(OffSeasonSummaryContext);

type ProviderProps = {
  children: ReactNode;
};

export const OffSeasonSummaryProvider: FC<ProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [selectedBoatHash, setSelectedBoatHash] = useState<string | null>(null);

  const openSummary = useCallback((boatHash: string) => {
    setSelectedBoatHash(boatHash);
    dispatch(open('offSeasonSummary'));
  }, [dispatch]);

  const closeSummary = useCallback(() => {
    setSelectedBoatHash(null);
    dispatch(close('offSeasonSummary'));
  }, [dispatch]);

  const value = useMemo(() => ({
    selectedBoatHash,
    openSummary,
    closeSummary,
  }), [selectedBoatHash, openSummary, closeSummary]);

  return (
    <OffSeasonSummaryContext.Provider value={value}>
      {children}
    </OffSeasonSummaryContext.Provider>
  );
};
